import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة العائد على الاستثمار ROI - احسب أرباح استثماراتك بدقة";
const DESCRIPTION =
  "استخدم حاسبة العائد على الاستثمار (ROI) المجانية لحساب كفاءة استثماراتك. احسب إجمالي العائد، النسبة المئوية للربح، والعائد السنوي بسهولة ودقة.";
const KEYWORDS =
  "حاسبة ROI, حساب العائد على الاستثمار, حاسبة الأرباح, تقييم الاستثمار, عائد الاستثمار السنوي, حساب الربح والخسارة, ROI Calculator Arabic";
const OG_URL = "https://do-calculate.com/calculator/ar/roi-calculator/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "حاسبة العائد على الاستثمار ROI",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "FinancialApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "الرئيسية",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "الحاسبات المالية",
        item: "https://do-calculate.com/ar/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "حاسبة العائد على الاستثمار",
        item: OG_URL,
      },
    ],
  },
];

function ROICalculator() {
  const [amountInvested, setAmountInvested] = useState("");
  const [amountReturned, setAmountReturned] = useState("");
  const [investmentPeriod, setInvestmentPeriod] = useState("");
  const [periodUnit, setPeriodUnit] = useState("years");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const calculateROI = () => {
    const invested = parseFloat(amountInvested);
    const returned = parseFloat(amountReturned);
    const period = parseFloat(investmentPeriod);

    if (isNaN(invested) || isNaN(returned) || invested <= 0) {
      setResult(null);
      return;
    }

    const totalProfit = returned - invested;
    const roiPercentage = (totalProfit / invested) * 100;

    let annualizedROI = null;
    if (!isNaN(period) && period > 0) {
      const years = periodUnit === "years" ? period : period / 12;
      // Formula: ((Amount Returned / Amount Invested) ^ (1 / Years) - 1) * 100
      annualizedROI = (Math.pow(returned / invested, 1 / years) - 1) * 100;
    }

    setResult({
      totalProfit,
      roiPercentage,
      annualizedROI,
      investmentGain: (returned / invested).toFixed(2),
    });
  };

  useEffect(() => {
    calculateROI();
  }, [amountInvested, amountReturned, investmentPeriod, periodUnit]);

  const handleClear = () => {
    setAmountInvested("");
    setAmountReturned("");
    setInvestmentPeriod("");
    setPeriodUnit("years");
    setResult(null);
    setError("");
  };

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>حاسبة العائد على الاستثمار ROI</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة العائد على الاستثمار (ROI)</strong> هي أداة مالية تُستخدم لتقييم كفاءة الاستثمار أو مقارنة كفاءة عدد من الاستثمارات المختلفة. يقيس العائد على الاستثمار مقدار العائد بالنسبة إلى تكلفة الاستثمار.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>بيانات الاستثمار</h2>
              </div>

              <div className="row mb-3">
                <div className="col-sm-12 col-md-6">
                  <label >المبلع المستثمر</label>
                  <input
                    type="number"
                    className="form-control"
                    value={amountInvested}
                    onChange={(e) => setAmountInvested(e.target.value)}
                    placeholder="مثال: 10000"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >المبلغ المسترد (القيمة النهائية)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={amountReturned}
                    onChange={(e) => setAmountReturned(e.target.value)}
                    placeholder="مثال: 12000"
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-sm-12 col-md-6">
                  <label >مدة الاستثمار (اختياري)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={investmentPeriod}
                    onChange={(e) => setInvestmentPeriod(e.target.value)}
                    placeholder="المدة"
                  />
                </div>
                <div className="col-sm-12 col-md-6">
                  <label >وحدة المدة</label>
                  <select
                    className="form-select"
                    value={periodUnit}
                    onChange={(e) => setPeriodUnit(e.target.value)}
                  >
                    <option value="years">سنوات</option>
                    <option value="months">أشهر</option>
                  </select>
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button onClick={handleClear} className="btn btn-secondary" style={{ padding: "10px 30px" }}>
                  امسح البيانات
                </button>
              </div>

              {result && (
                <div className="mt-4">
                  <h3 className="text-center mb-3">النتائج</h3>
                  <table className="table bmi-table">
                    <thead>
                      <tr>
                        <th >المؤشر</th>
                        <th >القيمة</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>إجمالي الربح/الخسارة</td>
                        <td style={{ fontWeight: "bold", color: result.totalProfit >= 0 ? "#1a5f3f" : "#d9534f" }}>
                          {result.totalProfit.toLocaleString()}
                        </td>
                      </tr>
                      <tr>
                        <td>نسبة العائد على الاستثمار (ROI)</td>
                        <td style={{ fontWeight: "bold" }}>{result.roiPercentage.toFixed(2)}%</td>
                      </tr>
                      {result.annualizedROI !== null && (
                        <tr>
                          <td>العائد السنوي (Annualized ROI)</td>
                          <td style={{ fontWeight: "bold", color: "#007bff" }}>
                            {result.annualizedROI.toFixed(2)}%
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>مضاعف الاستثمار</td>
                        <td>{result.investmentGain}x</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="info-sections-container">
              <div className="info-section">
                <div className="info-section-header">كيفية حساب العائد على الاستثمار (ROI)</div>
                <div className="info-section-body">
                  <p>تعتمد المعادلة الأساسية لحساب ROI على طرح التكلفة الأصلية للاستثمار من قيمته النهائية، ثم قسمة الناتج على التكلفة الأصلية:</p>
                  <div className="alert alert-info text-center" style={{ direction: "ltr" }}>
                    ROI = ((القيمة النهائية - التكلفة الأصلية) / التكلفة الأصلية) × 100
                  </div>
                  <p>تساعد هذه النسبة المستثمرين على معرفة مدى نجاح استثمار معين مقارنة بغيره من الفرص المتاحة.</p>
                </div>
              </div>

              <div className="info-section">
                <div className="info-section-header">ما هو العائد السنوي (Annualized ROI)؟</div>
                <div className="info-section-body">
                  <p>بينما يخبرك ROI البسيط بإجمالي الربح خلال كامل فترة الاستثمار، فإن العائد السنوي يوضح متوسط الربح الذي حققه الاستثمار في كل سنة. هذا مهم جداً للمقارنة بين استثمارات ذات مدد زمنية مختلفة.</p>
                  <ul>
                    <li>إذا كان الاستثمار لمدة 5 سنوات وحقق 50%، فإن ROI السنوي ليس 10% ببساطة، بل يتم حسابه باستخدام الفائدة المركبة.</li>
                    <li>يساعد العائد السنوي في اتخاذ قرارات مالية أكثر ذكاءً عند المفاضلة بين الأسهم، العقارات، أو السندات.</li>
                  </ul>
                </div>
              </div>
            </div>

            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
    </div>
  );
}

export default ROICalculator;